// Client-side DOCX export for LP Analysis
// Builds a styled .docx document matching the Python structure EXACTLY
// Updated with reordered sections
import {
  AlignmentType,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  Document,
  ImageRun,
  BorderStyle,
  WidthType,
  TableLayoutType,
  Header,
  Footer,
  PageBreak,
  Bookmark,
  InternalHyperlink,
  VerticalAlign
} from 'docx';
import askpotLogo from '../assets/images/askpot_logo_transparent.png';

const formatMarketSize = (value) => {
  if (!value || isNaN(value)) return '$0';
  const num = Number(value);
  if (num >= 1e9) {
    return `$${(num / 1e9).toFixed(2)}B`;
  } else if (num >= 1e6) {
    return `$${(num / 1e6).toFixed(2)}M`;
  } else if (num >= 1e3) {
    return `$${(num / 1e3).toFixed(2)}K`;
  } else {
    return `$${num.toLocaleString()}`;
  }
};

const createHeading = (text, level = HeadingLevel.HEADING_2, bookmarkId = null) => [
  new Paragraph({ text: '', spacing: { after: 400 } }), // Space BEFORE heading (bigger)
  new Paragraph({
    children: bookmarkId
      ? [
          new Bookmark({
            id: bookmarkId,
            children: [
              new TextRun({
                text: text || '',
                bold: true,
                color: level === HeadingLevel.HEADING_1 ? '2E7D32' : '388E3C',
                size: level === HeadingLevel.HEADING_1 ? 32 : 28
              })
            ]
          })
        ]
      : [
          new TextRun({
            text: text || '',
            bold: true,
            color: level === HeadingLevel.HEADING_1 ? '2E7D32' : '388E3C',
            size: level === HeadingLevel.HEADING_1 ? 32 : 28
          })
        ]
  }),
  new Paragraph({ text: '', spacing: { after: 200 } }) // Space AFTER heading (smaller)
];

const createSectionDivider = () =>
  new Paragraph({
    children: [
      new TextRun({
        text: '_______________________________________________',
        color: '2E7D32',
        size: 24
      })
    ],
    alignment: AlignmentType.CENTER,
    spacing: { before: 600, after: 600 }
  });

const createText = (text) =>
  new Paragraph({
    text: text ?? '',
    spacing: { before: 200, after: 200 }
  });

const createBulletList = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  // Remove duplicates and empty items
  const uniqueItems = [...new Set(items.filter((item) => item && item.trim && item.trim() !== ''))];

  return uniqueItems.map(
    (item) =>
      new Paragraph({
        text: typeof item === 'string' ? item : JSON.stringify(item),
        bullet: { level: 0 },
        spacing: { before: 120, after: 120 }
      })
  );
};

// Create table with proper styling like Python version
const createStyledTable = (headers, rows, customWidths = null) => {
  // Standard A4 page with 1" margins = ~9000 twips total width
  const totalWidth = 9000;

  // Use custom widths if provided, otherwise equal distribution
  const columnWidths = customWidths
    ? customWidths.map((width) => Math.floor(totalWidth * width))
    : headers.map(() => Math.floor(totalWidth / headers.length));

  const tableRows = [
    // Header row
    new TableRow({
      children: headers.map(
        (header, index) =>
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: header, bold: true })]
              })
            ],
            width: { size: columnWidths[index], type: WidthType.DXA },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 4 },
              bottom: { style: BorderStyle.SINGLE, size: 4 },
              left: { style: BorderStyle.SINGLE, size: 4 },
              right: { style: BorderStyle.SINGLE, size: 4 }
            }
          })
      )
    }),
    // Data rows
    ...rows.map(
      (row) =>
        new TableRow({
          children: row.map(
            (cell, index) =>
              new TableCell({
                children: [
                  new Paragraph({
                    text: String(cell ?? 'N/A')
                  })
                ],
                width: { size: columnWidths[index], type: WidthType.DXA },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 4 },
                  bottom: { style: BorderStyle.SINGLE, size: 4 },
                  left: { style: BorderStyle.SINGLE, size: 4 },
                  right: { style: BorderStyle.SINGLE, size: 4 }
                }
              })
          )
        })
    )
  ];

  return new Table({
    rows: tableRows,
    width: { size: totalWidth, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: columnWidths,
    margins: {
      top: 200,
      bottom: 200,
      left: 0,
      right: 0
    }
  });
};

const dataURLToUint8Array = async (dataURL) => {
  const res = await fetch(dataURL);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
};

// Create market projection chart manually using Canvas
const createMarketProjectionChart = (marketSize, cagr) => {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 360;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Calculate projection data
  const currentYear = new Date().getFullYear();
  const years = [];
  const values = [];

  for (let i = 0; i < 7; i++) {
    years.push(currentYear + i);
    values.push(marketSize * Math.pow(1 + cagr / 100, i));
  }

  // Chart dimensions
  const margin = { top: 40, right: 40, bottom: 60, left: 80 };
  const chartWidth = canvas.width - margin.left - margin.right;
  const chartHeight = canvas.height - margin.top - margin.bottom;

  // Find max value for scaling - start from 0 for better visualization
  const maxValue = Math.max(...values);
  const minValue = 0; // Always start from 0 for market size charts
  const valueRange = maxValue - minValue;

  // Draw bars
  const barWidth = (chartWidth / years.length) * 0.8;
  const barSpacing = chartWidth / years.length;

  values.forEach((value, index) => {
    const barHeight = ((value - minValue) / valueRange) * chartHeight;
    const x = margin.left + index * barSpacing + (barSpacing - barWidth) / 2;
    const y = margin.top + chartHeight - barHeight;

    // Bar
    ctx.fillStyle = '#8884d8';
    ctx.fillRect(x, y, barWidth, barHeight);

    // Bar border
    ctx.strokeStyle = '#6666aa';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, barWidth, barHeight);

    // Value label on top of each bar
    ctx.fillStyle = '#333';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    const labelValue = formatMarketSize(value);
    ctx.fillText(labelValue, x + barWidth / 2, y - 5);
  });

  // Grid lines
  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);

  // Horizontal grid lines
  for (let i = 0; i <= 5; i++) {
    const y = margin.top + (chartHeight / 5) * i;
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(margin.left + chartWidth, y);
    ctx.stroke();
  }

  // Vertical grid lines
  years.forEach((_, index) => {
    const x = margin.left + index * barSpacing + barSpacing / 2;
    ctx.beginPath();
    ctx.moveTo(x, margin.top);
    ctx.lineTo(x, margin.top + chartHeight);
    ctx.stroke();
  });

  ctx.setLineDash([]);

  // Axes
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 2;

  // Y-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top);
  ctx.lineTo(margin.left, margin.top + chartHeight);
  ctx.stroke();

  // X-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top + chartHeight);
  ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
  ctx.stroke();

  // Labels
  ctx.fillStyle = '#333';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';

  // X-axis labels (years)
  years.forEach((year, index) => {
    const x = margin.left + index * barSpacing + barSpacing / 2;
    const y = margin.top + chartHeight + 20;
    ctx.fillText(year.toString(), x, y);
  });

  // Y-axis labels (values)
  ctx.textAlign = 'right';
  for (let i = 0; i <= 5; i++) {
    const value = minValue + (valueRange / 5) * (5 - i);
    const y = margin.top + (chartHeight / 5) * i + 5;
    const formattedValue = formatMarketSize(value);
    ctx.fillText(formattedValue, margin.left - 10, y);
  }

  // Title
  ctx.textAlign = 'center';
  ctx.font = 'bold 16px Arial';
  ctx.fillText('Market Size Projection (7 Years)', canvas.width / 2, 25);

  // CAGR subtitle
  ctx.font = '12px Arial';
  ctx.fillStyle = '#666';
  ctx.fillText(`CAGR: ${cagr}%`, canvas.width / 2, 40);

  // Legend
  ctx.font = '12px Arial';
  ctx.fillStyle = '#8884d8';
  ctx.fillRect(margin.left, canvas.height - 40, 15, 15);
  ctx.fillStyle = '#333';
  ctx.textAlign = 'left';
  ctx.fillText('Market Size', margin.left + 20, canvas.height - 28);

  // CAGR info in legend area
  ctx.fillText(`Annual Growth: ${cagr}%`, margin.left + 120, canvas.height - 28);

  return canvas.toDataURL('image/png');
};

// Load Askpot logo
const loadAskpotLogo = async () => {
  try {
    const response = await fetch(askpotLogo);
    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Helper function to detect if ICP is B2C or B2B based on data structure
const detectCustomerType = (icp) => {
  // Check for B2C-specific fields
  if (icp?.purchaseMotivations?.emotionalTriggers || icp?.purchaseMotivations?.needsAndDesires || icp?.brandInteractionAndLoyalty) {
    return 'b2c';
  }

  // Check for B2B-specific fields
  if (icp?.professionalBackground || icp?.goalsAndObjectives?.businessObjectives || icp?.extendedCareer || icp?.buyingDecisionProcess) {
    return 'b2b';
  }

  // Default to B2C if unclear
  return 'b2c';
};

export async function exportLpAnalysisDocx(analysisData, extended, fileName) {
  const children = [];

  // Load logo
  const logoDataUrl = await loadAskpotLogo();
  let logoBytes = null;
  if (logoDataUrl) {
    logoBytes = await dataURLToUint8Array(logoDataUrl);
  }

  // Title page content - will be in separate section with vertical centering
  const titlePageContent = [
    // AskPot logo at the top
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 1200 },
      children: logoBytes
        ? [
            new ImageRun({
              data: logoBytes,
              transformation: { width: 200, height: 55 } // Larger logo for title page
            })
          ]
        : []
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
      children: [new TextRun({ text: `${analysisData?.['Service Name'] || 'Company Name'}`, size: 48, color: '6B8E23' })]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
      children: [new TextRun({ text: 'Analysis Report', size: 36, bold: true })]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: `Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
          size: 18
        })
      ]
    }),
    // Powered by AskPot AI tagline
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: 'Powered by Askpot AI', size: 16, color: '666666', italic: true })]
    })
  ];

  // Table of Contents page
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'Table of Contents', size: 32, bold: true, color: '2E7D32' })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 800, after: 600 }
    })
  );

  // TOC entries with clickable links
  const tocEntries = [
    { text: '1. Executive Summary', anchor: 'executive-summary' },
    { text: '2. Company Overview', anchor: 'company-overview' },
    { text: '3. Market Analysis', anchor: 'market-analysis' },
    { text: '4. Competitors', anchor: 'competitor-analysis' },
    { text: '5. SWOT Analysis', anchor: 'swot-analysis' },
    { text: '6. Customer Segmentation', anchor: 'customer-segmentation' },
    { text: '7. Ideal Customer Profile', anchor: 'icp' }
  ];

  tocEntries.forEach((entry, index) => {
    const [number, ...titleParts] = entry.text.split('. ');
    const title = titleParts.join('. ');
    const isLast = index === tocEntries.length - 1;

    children.push(
      new Paragraph({
        children: [
          new InternalHyperlink({
            children: [
              new TextRun({
                text: `${number}. ${title}`,
                size: 18,
                style: 'Hyperlink'
              })
            ],
            anchor: entry.anchor
          }),
          ...(isLast ? [new PageBreak()] : [])
        ],
        spacing: { after: 200 },
        indent: { left: 400 }
      })
    );
  });

  // 1. Executive Summary - EXACTLY like Python
  children.push(createSectionDivider());
  children.push(...createHeading('1. Executive Summary', HeadingLevel.HEADING_1, 'executive-summary'));

  if (analysisData) {
    // Company description
    children.push(...createHeading('Company Description'));
    children.push(createText(analysisData['Description'] || 'No description available.'));

    // Main value proposition
    children.push(...createHeading('Main Value Proposition'));
    children.push(createText(analysisData['Main Value Proposition'] || 'No value proposition available.'));

    // Industry
    children.push(...createHeading('Industry'));
    children.push(createText(analysisData['Industry'] || 'No industry information available.'));
  }

  // 2. Company Overview - EXACTLY like Python structure
  children.push(createSectionDivider());
  children.push(...createHeading('2. Company Overview', HeadingLevel.HEADING_1, 'company-overview'));

  if (analysisData) {
    // Positioning
    if (analysisData['Positioning']) {
      children.push(...createHeading('Positioning'));
      children.push(createText(analysisData['Positioning']));
    }

    // Tone of Voice
    if (analysisData['Tone of Voice']) {
      children.push(...createHeading('Tone of Voice'));
      children.push(createText(analysisData['Tone of Voice']));
    }

    // Pricing
    if (analysisData['Pricing']) {
      children.push(...createHeading('Pricing'));
      children.push(createText(analysisData['Pricing']));
    }

    // CTA
    if (analysisData['CTA']) {
      children.push(...createHeading('Call to Action'));
      children.push(createText(analysisData['CTA']));
    }

    // Strengths
    children.push(...createHeading('Strengths'));
    children.push(createText(analysisData['Strengths'] || 'No strengths information available.'));

    // Key Features
    children.push(...createHeading('Key Features'));
    children.push(createText(analysisData['Key Features'] || 'No key features available.'));

    // Trust Factors
    children.push(...createHeading('Trust Factors'));
    children.push(createText(analysisData['Trust Factors'] || 'No trust factors available.'));

    // Differentiation
    children.push(...createHeading('Differentiation'));
    children.push(createText(analysisData['Differentiation'] || 'No differentiation information available.'));

    // USP - moved from separate chapter
    const usp = extended?.usp;
    if (usp) {
      if (usp.USP) {
        children.push(...createHeading('Unique Selling Propositions'));
        children.push(...createBulletList(usp.USP));
      }

      if (usp.DISTINCTION) {
        children.push(...createHeading('Distinctions'));
        children.push(...createBulletList(usp.DISTINCTION));
      }
    }
  }

  // 3. Market Analysis - EXACTLY like Python
  children.push(createSectionDivider());
  children.push(...createHeading('3. Market Analysis', HeadingLevel.HEADING_1, 'market-analysis'));

  const market = extended?.market_overview;
  if (market) {
    // Market size and growth
    children.push(...createHeading('Market Size and Growth'));
    children.push(createText(`Current Market Size: ${formatMarketSize(market.CurrentMarketSize)}`));
    children.push(createText(`CAGR: ${market.CAGR || 0}%`));
    children.push(createText(market['Market Size and Growth'] || 'No market size information available.'));

    // Market growth projection chart (7 years) - MANUALLY CREATED
    children.push(...createHeading(`Market Size Projection (7 Years) - CAGR: ${market.CAGR || 0}%`));

    // Create chart manually with actual data
    const chartDataUrl = createMarketProjectionChart(market.CurrentMarketSize || 1000000, market.CAGR || 10);

    const chartBytes = await dataURLToUint8Array(chartDataUrl);
    children.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: chartBytes,
            transformation: { width: 640, height: 360 }
          })
        ]
      })
    );
    console.log('Market projection chart created and added to DOCX');

    // Key trends
    children.push(...createHeading('Key Trends'));
    children.push(createText(market['Key Trends'] || 'No trends information available.'));

    // Opportunities
    children.push(...createHeading('Opportunities'));
    children.push(createText(market['Opportunities'] || 'No opportunities information available.'));

    // Challenges
    children.push(...createHeading('Challenges'));
    children.push(createText(market['Challenges'] || 'No challenges information available.'));
  }

  // Market trends and benchmarks - EXACTLY like Python
  const trendsData = extended?.market_trends_and_benchmarks;
  if (trendsData) {
    children.push(...createHeading('Market Trends'));
    if (trendsData.trends) {
      trendsData.trends.forEach((trend, i) => {
        children.push(...createHeading(`Trend ${i + 1}: ${trend.title || 'Untitled'}`, HeadingLevel.HEADING_3));
        children.push(createText(trend.description || 'No description available.'));
        children.push(createText(`Industry Relevance: ${trend.industry_relevance || 'N/A'}`));
      });
    }

    children.push(...createHeading('Industry Benchmarks'));
    if (trendsData.benchmarks) {
      trendsData.benchmarks.forEach((benchmark) => {
        children.push(
          ...createHeading(`${benchmark.metric_value || 'N/A'} ${benchmark.metric_name || 'Untitled'}`, HeadingLevel.HEADING_3)
        );
        if (benchmark.description && benchmark.description !== 'No description available.') {
          children.push(createText(benchmark.description));
        }
      });
    }
  }

  // 4. Competitors - EXACTLY like Python
  children.push(createSectionDivider());
  children.push(...createHeading('4. Competitors', HeadingLevel.HEADING_1, 'competitor-analysis'));

  // Debug competitors data structure
  console.log('Extended data for competitors:', extended);
  console.log('Competitors search task:', extended?.competitors_search_task);
  console.log('Competitors search:', extended?.competitors_search);

  // Try multiple possible paths for competitors data
  const competitorsData =
    extended?.competitors_search_task?.results?.final_answer?.result ||
    extended?.competitors_search?.results?.final_answer?.result ||
    extended?.competitors_search_task?.results ||
    extended?.competitors_search?.results ||
    extended?.competitors_search_task ||
    extended?.competitors_search ||
    extended?.competitors;

  // console.log('Competitors data found:', competitorsData);

  const competitors = competitorsData?.results || competitorsData?.competitors || competitorsData || [];
  console.log('Final competitors array:', competitors);

  if (Array.isArray(competitors) && competitors.length > 0) {
    // Main competitors table
    const competitorRows = competitors.map((comp) => [
      comp.title || 'N/A',
      comp.description || 'N/A',
      String(comp.validity_score || 'N/A')
    ]);

    children.push(
      createStyledTable(
        ['Company', 'Description', 'Validity Score'],
        competitorRows,
        [0.25, 0.5, 0.25] // Company: 25%, Description: 50%, Validity Score: 25%
      )
    );

    // Emerging competitors
    const emerging = competitorsData?.emerging || [];
    if (Array.isArray(emerging) && emerging.length > 0) {
      children.push(...createHeading('Emerging Competitors'));
      const emergingRows = emerging.map((comp) => [comp.title || 'N/A', comp.description || 'N/A', String(comp.validity_score || 'N/A')]);

      children.push(
        createStyledTable(
          ['Company', 'Description', 'Validity Score'],
          emergingRows,
          [0.25, 0.5, 0.25] // Company: 25%, Description: 50%, Validity Score: 25%
        )
      );
    }
  }

  // 5. SWOT Analysis - EXACTLY like Python
  children.push(createSectionDivider());
  children.push(...createHeading('5. SWOT Analysis', HeadingLevel.HEADING_1, 'swot-analysis'));

  const swot = extended?.swot;
  if (swot) {
    ['Strengths', 'Weaknesses', 'Opportunities', 'Threats'].forEach((category) => {
      children.push(...createHeading(category));
      if (swot[category]) {
        children.push(...createBulletList(swot[category]));
      }
    });
  }

  // 6. Customer Segmentation - EXACTLY like Python
  children.push(createSectionDivider());
  children.push(...createHeading('6. Customer Segmentation', HeadingLevel.HEADING_1, 'customer-segmentation'));

  const segments = extended?.segmentation?.final_segments || [];
  if (Array.isArray(segments) && segments.length > 0) {
    // Create segments overview table
    const segmentRows = segments.map((segment) => [
      segment.name || 'N/A',
      segment.customer_type || 'N/A',
      String(segment.validity_score || 'N/A'),
      segment.JTBD || 'N/A',
      segment.core_characteristic || 'N/A'
    ]);

    children.push(createStyledTable(['Segment', 'Customer Type', 'Validity Score', 'JTBD', 'Core Characteristic'], segmentRows));

    // Detailed segment analysis - EXACTLY like Python
    segments.forEach((segment, i) => {
      children.push(...createHeading(`Segment ${i + 1}: ${segment.name || 'Untitled'}`, HeadingLevel.HEADING_2));

      // Segment overview line - BOLD
      const validityScore = segment.validity_score || 'N/A';
      const customerType = segment.customer_type || 'N/A';
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `Validity Score: ${validityScore} | Type: ${customerType}`, bold: true })],
          spacing: { before: 200, after: 200 }
        })
      );

      // JTBD - only key is bold
      if (segment.JTBD) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'JTBD: ', bold: true }), new TextRun({ text: segment.JTBD })],
            spacing: { before: 200, after: 200 }
          })
        );
      }

      // Core Characteristic - as subtitle
      if (segment.core_characteristic) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Core Characteristic', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        children.push(createText(segment.core_characteristic));
      }

      // Decision drivers - subtitle
      if (segment.decision_drivers) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Decision Drivers', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        children.push(...createBulletList(segment.decision_drivers));
      }

      // Purchase behavior - subtitle with bold keys
      if (segment.purchase_behavior) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Purchase Behavior', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        Object.entries(segment.purchase_behavior).forEach(([key, value]) => {
          const formattedKey = key.replace(/_/g, ' ').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
          children.push(
            new Paragraph({
              children: [new TextRun({ text: `${formattedKey}: `, bold: true }), new TextRun({ text: `${value}` })],
              spacing: { before: 200, after: 200 }
            })
          );
        });
      }
    });
  }

  // 7. Ideal Customer Profile (ICP) - Handle both B2C and B2B
  children.push(createSectionDivider());
  children.push(...createHeading('7. Ideal Customer Profile', HeadingLevel.HEADING_1, 'icp'));

  const icp = extended?.icp;
  if (icp && typeof icp === 'object') {
    Object.entries(icp).forEach(([segmentId, profile]) => {
      if (!profile || typeof profile !== 'object') return;

      // Detect customer type (B2C or B2B)
      const customerType = detectCustomerType(profile);

      children.push(...createHeading(`ICP: ${profile.name || segmentId}`, HeadingLevel.HEADING_2));

      // Basic info as formatted text lines (common to both B2C and B2B)
      const gender = profile.gender || 'N/A';
      const age = profile.age || 'N/A';
      const location = profile.location || 'N/A';
      const familyStatus = profile.familyStatus || 'N/A';
      const education = profile.education || 'N/A';

      // First line: Gender, Age, Location, Status
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: 'Gender: ', bold: true }),
            new TextRun({ text: `${gender} | ` }),
            new TextRun({ text: 'Age: ', bold: true }),
            new TextRun({ text: `${age} | ` }),
            new TextRun({ text: 'Location: ', bold: true }),
            new TextRun({ text: `${location} | ` }),
            new TextRun({ text: 'Status: ', bold: true }),
            new TextRun({ text: familyStatus })
          ],
          spacing: { before: 200, after: 200 }
        })
      );

      // Second line: Education
      if (education && education !== 'N/A') {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Education: ', bold: true }), new TextRun({ text: education })],
            spacing: { before: 200, after: 200 }
          })
        );
      }

      // B2B-specific sections
      if (customerType === 'b2b') {
        // Professional Summary
        if (profile.professionalSummary) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Professional Summary', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );
          children.push(createText(profile.professionalSummary));
        }

        // Professional Background
        if (profile.professionalBackground) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Professional Background', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const bg = profile.professionalBackground;
          if (bg.industry) children.push(createText(`Industry: ${bg.industry}`));
          if (bg.companySize) children.push(createText(`Company Size: ${bg.companySize}`));
          if (bg.jobTitle) children.push(createText(`Job Title: ${bg.jobTitle}`));
          if (bg.experience) children.push(createText(`Experience: ${bg.experience}`));

          if (bg.keyResponsibilities && Array.isArray(bg.keyResponsibilities)) {
            children.push(createText('Key Responsibilities:'));
            children.push(...createBulletList(bg.keyResponsibilities));
          }
        }

        // Extended Career
        if (profile.extendedCareer) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Career Details', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const career = profile.extendedCareer;
          if (career.seniorityLevel) children.push(createText(`Seniority Level: ${career.seniorityLevel}`));
          if (career.personalGoals && Array.isArray(career.personalGoals)) {
            children.push(createText('Personal Goals:'));
            children.push(...createBulletList(career.personalGoals));
          }
          if (career.professionalGoals && Array.isArray(career.professionalGoals)) {
            children.push(createText('Professional Goals:'));
            children.push(...createBulletList(career.professionalGoals));
          }
          if (career.industryImpactGoals) children.push(createText(`Industry Impact Goals: ${career.industryImpactGoals}`));
        }

        // Goals and Objectives
        if (profile.goalsAndObjectives) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Goals & Objectives', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const goals = profile.goalsAndObjectives;
          if (goals.primaryGoals) children.push(createText(`Primary Goals: ${goals.primaryGoals}`));

          if (goals.businessObjectives && typeof goals.businessObjectives === 'object') {
            children.push(createText('Business Objectives:'));
            Object.entries(goals.businessObjectives).forEach(([key, obj]) => {
              if (obj && obj.description) {
                children.push(createText(`• ${key}: ${obj.description} (Score: ${obj.final_score || 'N/A'}/10)`));
              }
            });
          }

          if (goals.successMetrics && Array.isArray(goals.successMetrics)) {
            children.push(createText('Success Metrics:'));
            goals.successMetrics.forEach((metric) => {
              if (metric && metric.description) {
                children.push(createText(`• ${metric.description} (Score: ${metric.final_score || 'N/A'}/10)`));
              }
            });
          }
        }

        // Values and Motivations
        if (profile.valuesAndMotivations) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Values & Motivations', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const values = profile.valuesAndMotivations;
          if (values.coreValues && Array.isArray(values.coreValues)) {
            children.push(createText('Core Values:'));
            children.push(...createBulletList(values.coreValues));
          }
          if (values.motivators) children.push(createText(`Motivators: ${values.motivators}`));
          if (values.aspirations) children.push(createText(`Aspirations: ${values.aspirations}`));
        }

        // Buying Decision Process
        if (profile.buyingDecisionProcess) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Buying Decision Process', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const buying = profile.buyingDecisionProcess;
          if (buying.decisionMakingStyle) children.push(createText(`Decision Making Style: ${buying.decisionMakingStyle}`));
          if (buying.primaryMotivations && Array.isArray(buying.primaryMotivations)) {
            children.push(createText('Primary Motivations:'));
            children.push(...createBulletList(buying.primaryMotivations));
          }
          if (buying.barriersToPurchase && Array.isArray(buying.barriersToPurchase)) {
            children.push(createText('Barriers to Purchase:'));
            children.push(...createBulletList(buying.barriersToPurchase));
          }
          if (buying.valuedFeatures && Array.isArray(buying.valuedFeatures)) {
            children.push(createText('Valued Features:'));
            children.push(...createBulletList(buying.valuedFeatures));
          }
          if (buying.preferredCompetitors && Array.isArray(buying.preferredCompetitors)) {
            children.push(createText('Preferred Competitors:'));
            children.push(...createBulletList(buying.preferredCompetitors));
          }
          if (buying.likelihoodToRecommend) children.push(createText(`Likelihood to Recommend: ${buying.likelihoodToRecommend}`));
        }
      }

      // B2C-specific sections or common sections
      if (customerType === 'b2c') {
        // Purchase Motivations (B2C)
        if (profile.purchaseMotivations) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Purchase Motivations', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const motivations = profile.purchaseMotivations;
          if (motivations.emotionalTriggers && Array.isArray(motivations.emotionalTriggers)) {
            children.push(createText('Emotional Triggers:'));
            motivations.emotionalTriggers.forEach((trigger) => {
              if (trigger && trigger.description) {
                children.push(createText(`• ${trigger.description} (Score: ${trigger.final_score || 'N/A'}/10)`));
              }
            });
          }

          if (motivations.needsAndDesires && Array.isArray(motivations.needsAndDesires)) {
            children.push(createText('Needs & Desires:'));
            motivations.needsAndDesires.forEach((need) => {
              if (need && need.description) {
                children.push(createText(`• ${need.description} (Score: ${need.final_score || 'N/A'}/10)`));
              }
            });
          }
        }

        // Brand Interaction & Loyalty (B2C)
        if (profile.brandInteractionAndLoyalty) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Brand Interaction & Loyalty', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const brand = profile.brandInteractionAndLoyalty;
          if (brand.discoveryChannel) children.push(createText(`Discovery Channel: ${brand.discoveryChannel}`));
          if (brand.conversionTriggers && Array.isArray(brand.conversionTriggers)) {
            children.push(createText('Conversion Triggers:'));
            children.push(...createBulletList(brand.conversionTriggers));
          }
          if (brand.postPurchaseBehavior) children.push(createText(`Post Purchase Behavior: ${brand.postPurchaseBehavior}`));

          if (brand.customerJourneyStages && typeof brand.customerJourneyStages === 'object') {
            children.push(createText('Customer Journey Stages:'));
            Object.entries(brand.customerJourneyStages).forEach(([stage, description]) => {
              children.push(createText(`• ${stage}: ${description}`));
            });
          }
        }

        // Values and Brand Affiliation (B2C)
        if (profile.valuesAndBrandAffiliation) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Values & Brand Affiliation', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const values = profile.valuesAndBrandAffiliation;
          if (values.coreValues && Array.isArray(values.coreValues)) {
            children.push(createText('Core Values:'));
            children.push(...createBulletList(values.coreValues));
          }
          if (values.brandLoyalty) children.push(createText(`Brand Loyalty: ${values.brandLoyalty}`));
          if (values.preferredBrands && Array.isArray(values.preferredBrands)) {
            children.push(createText('Preferred Brands:'));
            children.push(...createBulletList(values.preferredBrands));
          }
        }

        // Buying Triggers and Objections (B2C)
        if (profile.buyingTriggersAndObjections) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Buying Triggers & Objections', bold: true, size: 22 })],
              spacing: { before: 300, after: 100 }
            })
          );

          const buying = profile.buyingTriggersAndObjections;
          if (buying.buyingTriggers && Array.isArray(buying.buyingTriggers)) {
            children.push(createText('Buying Triggers:'));
            children.push(...createBulletList(buying.buyingTriggers));
          }
          if (buying.commonObjections && Array.isArray(buying.commonObjections)) {
            children.push(createText('Common Objections:'));
            children.push(...createBulletList(buying.commonObjections));
          }
        }
      }

      // Common sections for both B2C and B2B

      // Lifestyle summary
      if (profile.lifestyleSummary) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Lifestyle Summary', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        children.push(createText(profile.lifestyleSummary));
      }

      // Challenges and Pain Points (common to both B2C and B2B)
      if (profile.challengesAndPainPoints) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Challenges & Pain Points', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );

        const challenges = profile.challengesAndPainPoints;
        if (challenges.painPoints && Array.isArray(challenges.painPoints)) {
          children.push(createText('Pain Points:'));
          challenges.painPoints.forEach((point) => {
            if (point && point.description) {
              children.push(createText(`• ${point.description} (Score: ${point.final_score || 'N/A'}/10)`));
            }
          });
        }

        if (challenges.obstacles && Array.isArray(challenges.obstacles)) {
          children.push(createText('Obstacles:'));
          challenges.obstacles.forEach((obstacle) => {
            if (obstacle && obstacle.description) {
              children.push(createText(`• ${obstacle.description} (Score: ${obstacle.final_score || 'N/A'}/10)`));
            }
          });
        }

        if (challenges.needs && Array.isArray(challenges.needs)) {
          children.push(createText('Needs:'));
          challenges.needs.forEach((need) => {
            if (need && need.description) {
              children.push(createText(`• ${need.description} (Score: ${need.final_score || 'N/A'}/10)`));
            }
          });
        }
      }

      // Financial profile
      if (profile.financialProfile) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Financial Profile', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        Object.entries(profile.financialProfile).forEach(([key, value]) => {
          children.push(createText(`${key.replace(/_/g, ' ').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1))}: ${value}`));
        });
      }

      // Digital and Social
      if (profile.digitalAndSocial) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Digital and Social', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        const digital = profile.digitalAndSocial;

        if (digital.onlineCommunities) {
          children.push(createText('Online Communities:'));
          children.push(...createBulletList(digital.onlineCommunities));
        }

        if (digital.followedInfluencers) {
          children.push(createText('Followed Influencers:'));
          children.push(...createBulletList(digital.followedInfluencers));
        }

        if (digital.socialMediaPlatforms) {
          children.push(createText('Social Media Platforms:'));
          children.push(...createBulletList(digital.socialMediaPlatforms));
        }
      }

      // Behaviors and Habits
      if (profile.behaviorsAndHabits) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Behaviors and Habits', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        const behaviors = profile.behaviorsAndHabits;

        if (behaviors.technologyUsage) {
          children.push(createText('Technology Usage:'));
          children.push(...createBulletList(behaviors.technologyUsage));
        }

        if (behaviors.mediaConsumption) {
          children.push(createText('Media Consumption:'));
          children.push(...createBulletList(behaviors.mediaConsumption));
        }

        if (behaviors.shoppingBehavior) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Shopping Behavior', bold: true, size: 18 })],
              spacing: { before: 200, after: 50 }
            })
          );
          children.push(createText(behaviors.shoppingBehavior));
        }
      }

      // Extended Psychographics (common to both B2C and B2B)
      if (profile.extendedPsychographics) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Extended Psychographics', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        const psychographics = profile.extendedPsychographics;

        if (psychographics.lifestyleRoutine) {
          children.push(createText(`Lifestyle Routine: ${psychographics.lifestyleRoutine}`));
        }

        if (psychographics.personalityTraits) {
          children.push(createText('Personality Traits:'));
          children.push(...createBulletList(psychographics.personalityTraits));
        }

        if (psychographics.hobbiesAndInterests) {
          children.push(createText('Hobbies and Interests:'));
          children.push(...createBulletList(psychographics.hobbiesAndInterests));
        }
      }

      // Preferred Communication Channels (common to both B2C and B2B)
      if (profile.preferredCommunicationChannels) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Preferred Communication Channels', bold: true, size: 22 })],
            spacing: { before: 300, after: 100 }
          })
        );
        const comm = profile.preferredCommunicationChannels;

        if (comm.channels) {
          children.push(createText('Channels:'));
          children.push(...createBulletList(comm.channels));
        }

        if (comm.contentPreferences) {
          children.push(createText('Content Preferences:'));
          children.push(...createBulletList(comm.contentPreferences));
        }
      }
    });
  }

  // Build document with multiple sections
  const doc = new Document({
    sections: [
      // Title page section with vertical centering
      {
        children: titlePageContent,
        properties: {
          verticalAlign: VerticalAlign.CENTER
        }
      },
      // Rest of document with header/footer
      {
        children,
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: logoBytes ? [new ImageRun({ data: logoBytes, transformation: { width: 146, height: 40 } })] : [],
                alignment: AlignmentType.RIGHT,
                spacing: { after: 600 }
              })
            ]
          })
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'This analysis report was generated by Askpot AI. Powered by advanced AI technology to provide actionable insights for your business growth and strategic planning. For more in-depth analysis visit ',
                    color: '999999',
                    size: 18
                  }),
                  new TextRun({
                    text: 'askpot.com',
                    color: '999999',
                    size: 18,
                    bold: true
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 200 }
              })
            ]
          })
        }
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'lp-analysis.docx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default exportLpAnalysisDocx;
